<script setup lang="ts">
  import { computed, getCurrentInstance, nextTick, ref } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { questionView } from '@/domain/menu-import/question-copy';
  import type {
    IQuestionFormat,
    IQuestionOptionView,
    TQuestionReveal,
  } from '@/domain/menu-import/question-copy';
  import {
    DRAFT_NAME_MAX,
    cleanDraftName,
  } from '@/domain/menu-import/workspace-view';
  import { listJoin } from '@/domain/store-setup/display-names';
  import { currencyPluralName, formatMoney } from '@/domain/store-setup/money';
  import type {
    IAnswerQuestionDto,
    IDraftItem,
    IMenuDraft,
    TDraftQuestion,
  } from '@/interfaces/IMenuImport';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «Una pregunta» (plan §4.5, §4.5.3): a question the AI could not settle,
   * rendered from i18n with one button per answer. «Solo a algunos» first
   * shows the dishes to tick, «Nueva categoría» first asks its name. A
   * blocking question carries «Responda para publicar».
   */
  const props = defineProps<{
    question: TDraftQuestion;
    draft: IMenuDraft;
    currency: string;
    country: string;
    /** An answer is being saved. */
    busy: boolean;
  }>();
  const emit = defineEmits<{
    (e: 'answer', payload: IAnswerQuestionDto): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const inputId = nextDomId('menu-question-category');

  /** The option waiting for its extra input. */
  const reveal = ref<TQuestionReveal | null>(null);
  const picked = ref<string[]>(props.question.itemIds.slice());
  const categoryName = ref('');

  const format = computed<IQuestionFormat>(() => {
    const locale = proxy.$i18n.locale;
    return {
      money: (value) => formatMoney(value, props.currency, props.country),
      currencyName: (code) => currencyPluralName(code, locale),
      list: (items) => listJoin(items, locale),
    };
  });
  const view = computed(() =>
    questionView(props.question, props.draft, format.value)
  );
  /** Two-form texts («¿Ocultamos el plato…? | …los {count} platos…?») carry their count. */
  const text = computed(() => {
    const { textKey, textParams, textCount } = view.value;
    return textCount === undefined
      ? tr(textKey, textParams)
      : String(proxy.$tc(textKey, textCount, textParams));
  });
  /** The dishes «Solo a algunos» lets the owner tick. */
  const dishes = computed(() =>
    props.question.itemIds
      .map((id) => props.draft.items[id])
      .filter((item): item is IDraftItem => item !== undefined)
  );
  const cleanCategory = computed(() => cleanDraftName(categoryName.value));

  function optionLabel(option: IQuestionOptionView): string {
    return option.labelKey !== null
      ? tr(option.labelKey, option.labelParams)
      : option.label ?? '';
  }

  async function choose(option: IQuestionOptionView) {
    if (props.busy) return;
    if (option.reveals === null) {
      emit('answer', { answer: option.answer });
      return;
    }
    reveal.value = reveal.value === option.reveals ? null : option.reveals;
    if (reveal.value !== 'categoryName') return;
    await nextTick();
    const input = document.getElementById(inputId);
    if (input instanceof HTMLInputElement) input.focus();
  }

  function togglePicked(itemId: string, checked: boolean) {
    const others = picked.value.filter((id) => id !== itemId);
    picked.value = checked ? [...others, itemId] : others;
  }

  function onCheckbox(itemId: string, event: Event) {
    const target = event.target;
    if (target instanceof HTMLInputElement)
      togglePicked(itemId, target.checked);
  }

  function submitSome() {
    if (props.busy || picked.value.length === 0) return;
    // In the dishes' order, whatever the ticking order.
    const itemIds = props.question.itemIds.filter((id) =>
      picked.value.includes(id)
    );
    emit('answer', { answer: 'some', itemIds });
  }

  function submitCategory() {
    const name = cleanCategory.value;
    if (props.busy || name === null) return;
    emit('answer', { answer: 'new', categoryName: name });
  }
</script>

<template>
  <section class="v2-menu-question-card" :data-question-id="question.id">
    <header class="card-head">
      <PosUiIcon name="help-circle-outline" class="head-icon" />
      <span class="head-label">{{ $t('menuImport.questions.header') }}</span>
      <PosUiBadge v-if="question.blocking" color="warning" size="sm">{{
        $t('menuImport.questions.blocking')
      }}</PosUiBadge>
    </header>
    <p class="card-text">{{ text }}</p>
    <div class="card-options">
      <PosUiButton
        v-for="option in view.options"
        :key="option.answer"
        class="option-button"
        :class="{
          'is-revealed': option.reveals !== null && option.reveals === reveal,
        }"
        color="neutral"
        variant="outline"
        :disabled="busy"
        :aria-expanded="
          option.reveals === null
            ? undefined
            : option.reveals === reveal
            ? 'true'
            : 'false'
        "
        @click="choose(option)"
        >{{ optionLabel(option) }}</PosUiButton
      >
    </div>
    <div v-if="reveal === 'items'" class="card-reveal">
      <p class="reveal-label">
        {{ $t('menuImport.questions.addonScope.pick') }}
      </p>
      <ul class="dish-checks">
        <li v-for="dish in dishes" :key="dish.id">
          <label class="dish-check">
            <input
              type="checkbox"
              class="check-input"
              :checked="picked.includes(dish.id)"
              :disabled="busy"
              @change="onCheckbox(dish.id, $event)"
            />
            <span class="check-name">{{ dish.name }}</span>
          </label>
        </li>
      </ul>
      <PosUiButton
        color="neutral"
        :disabled="busy || picked.length === 0"
        @click="submitSome"
        >{{ $t('menuImport.questions.addonScope.done') }}</PosUiButton
      >
    </div>
    <div v-if="reveal === 'categoryName'" class="card-reveal is-inline">
      <PosUiInput
        :id="inputId"
        class="category-input"
        :value="categoryName"
        :maxlength="DRAFT_NAME_MAX"
        :placeholder="$t('menuImport.questions.category.newPlaceholder')"
        :aria-label="$t('menuImport.questions.category.newPlaceholder')"
        :disabled="busy"
        autocomplete="off"
        @input="categoryName = $event"
        @enter="submitCategory"
      />
      <PosUiButton
        color="neutral"
        :disabled="busy || cleanCategory === null"
        @click="submitCategory"
        >{{ $t('menuImport.questions.category.done') }}</PosUiButton
      >
    </div>
  </section>
</template>

<style scoped lang="scss">
  .v2-menu-question-card {
    @apply tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-sky-200 tw-bg-sky-50 tw-p-4;
    .card-head {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;
    }
    .head-icon {
      @apply tw-text-base tw-text-sky-700;
    }
    .head-label {
      @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-sky-700;
    }
    .card-text {
      @apply tw-m-0 tw-text-sm tw-text-neutral-900 md:tw-text-base;
    }
    .card-options {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
    // Long answers («No, dejar separados», category names) wrap instead of overflowing.
    .option-button {
      @apply tw-h-auto tw-min-h-11 tw-max-w-full tw-whitespace-normal tw-py-2 tw-text-left;
      &.is-revealed {
        @apply tw-border-neutral-900 tw-text-neutral-900;
      }
    }
    .card-reveal {
      @apply tw-flex tw-flex-col tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-sky-200 tw-bg-white tw-p-3;
      &.is-inline {
        @apply tw-flex-row tw-flex-wrap tw-items-center;
      }
    }
    .reveal-label {
      @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .dish-checks {
      @apply tw-m-0 tw-flex tw-w-full tw-list-none tw-flex-col tw-p-0;
    }
    .dish-check {
      @apply tw-flex tw-min-h-11 tw-cursor-pointer tw-items-center tw-gap-3 tw-text-sm tw-text-neutral-900;
    }
    .check-input {
      @apply tw-h-5 tw-w-5 tw-shrink-0 tw-cursor-pointer;
      accent-color: theme('colors.neutral.900');
    }
    .check-name {
      @apply tw-min-w-0 tw-break-words;
    }
    .category-input {
      @apply tw-min-w-0 tw-flex-1;
      min-width: min(14rem, 100%);
    }
  }
</style>
